import { sortAsc } from '@strata/helper-functions';
import { Checkbox } from '@strata/react-checkbox-list';
import { DateInputFormik } from '@strata/react-date-time';
import { DialogWithForm } from '@strata/react-dialog';
import { InlineMessage } from '@strata/react-inline-message';
import { validationStrings } from '@strata/strata-ui-strings';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import * as Yup from 'yup';

import { PreferenceWindowRows } from 'pages/therapyGroups/dialogs/PreferenceWindowRows';
import { handleError } from 'services/ErrorManagement';
import { useGet } from 'services/Http';
import { mapPayerSegment } from 'services/PayerSegmentSupport';
import { productDescriptionFor } from 'services/TherapyGroupPreferenceHelpers';
import {
    clientPreferenceChanges,
    endsBeforeStart,
    hasOverlappingRows,
    preferenceRowsFor,
    startsBeforeToday,
} from 'services/TherapyGroupPreferenceRows';

const BRAND_LABELS = [
    [true, 'Brand'],
    [false, 'Generic'],
];

const flagForConfig = (config, productFlag) => config.is_brand_default ?? productFlag;

const flagsBySegment = (payerConfigs, productFlag) =>
    (payerConfigs ?? []).reduce(
        (bySegment, config) => ({
            ...bySegment,
            [config.segment_id]: [
                ...(bySegment[config.segment_id] ?? []),
                flagForConfig(config, productFlag),
            ],
        }),
        {},
    );

const orderedBrandLabel = (flags) =>
    BRAND_LABELS.filter(([flag]) => flags.includes(flag))
        .map(([, label]) => label)
        .join(' / ') || undefined;

const segmentOption = (segment, bySegment, productFlag) => {
    const rightSecondaryData = orderedBrandLabel(bySegment[segment.segment_id] ?? [productFlag]);
    return {
        ...mapPayerSegment(segment),
        payerName: segment.payer_name ?? segment.payer_id,
        ...(rightSecondaryData ? { rightSecondaryData } : {}),
    };
};

const segmentOptions = (payerSegments, productConfig) => {
    const productFlag = productConfig?.is_brand_default;
    const bySegment = flagsBySegment(productConfig?.payer_configs, productFlag);
    return (payerSegments ?? [])
        .map((segment) => segmentOption(segment, bySegment, productFlag))
        .sort(
            (a, b) => sortAsc(a.payerName, b.payerName) || sortAsc(a.segment_name, b.segment_name),
        );
};

// A stable default keeps the memoised initial values from changing on every render.
const NO_WINDOWS = [];

const EndBeforeStartMessage = 'End must be on or after Start.';
const StartInPastMessage = 'Start cannot be in the past.';
const OverlappingRowsMessage = 'Rows for the same payer segment cannot overlap.';
const EndInPastMessage = 'End cannot be in the past for a preference already in effect.';

const rowSchema = Yup.object({
    segmentId: Yup.string().nullable().required(validationStrings.requiredField.errorMessage),
    start: DateInputFormik.validator()
        .required(validationStrings.requiredField.errorMessage)
        .test(
            'start-not-past',
            StartInPastMessage,
            (start, { parent }) => parent.hasStarted || !startsBeforeToday(start),
        ),
    end: DateInputFormik.validator()
        .nullable()
        .test(
            'end-after-start',
            EndBeforeStartMessage,
            (end, { parent }) => !end || !parent.start || !endsBeforeStart(end, parent.start),
        )
        .test(
            'started-end-not-past',
            EndInPastMessage,
            (end, { parent }) => !end || !parent.hasStarted || !startsBeforeToday(end),
        ),
});

const preferenceSchema = Yup.object({
    rows: Yup.array()
        .of(rowSchema)
        .test('no-overlap', OverlappingRowsMessage, (rows) => !hasOverlappingRows(rows ?? [])),
});

export const EditClientPreferenceDialog = ({
    isOpen,
    oid = undefined,
    parentProducts = undefined,
    preferences,
    payerSegments = undefined,
    windows = NO_WINDOWS,
    onApply,
    onClose,
}) => {
    const description = productDescriptionFor(parentProducts, oid);
    const isCurrentGlobal = preferences.global === oid;
    const productConfig = useGet(
        isOpen && oid ? `/reference/product_benefit_configs/edit/${oid}` : null,
    );
    const options = segmentOptions(payerSegments, productConfig);
    const replacementDescription = preferences.global
        ? productDescriptionFor(parentProducts, preferences.global)
        : '';
    const initialValues = useMemo(
        () => ({
            isGlobalDefault: isCurrentGlobal,
            rows: preferenceRowsFor(oid, preferences, windows),
        }),
        [isCurrentGlobal, oid, preferences, windows],
    );

    return (
        <Formik
            initialValues={initialValues}
            enableReinitialize
            validationSchema={preferenceSchema}
            onSubmit={async (values) => {
                try {
                    await onApply(
                        clientPreferenceChanges({
                            oid,
                            isCurrentGlobal,
                            isGlobalDefault: values.isGlobalDefault,
                            initialRows: initialValues.rows,
                            rows: values.rows,
                        }),
                    );
                    onClose();
                } catch (err) {
                    handleError('Unable to save client preference.', err);
                }
            }}
        >
            {(formik) => (
                <DialogWithForm
                    dialogTitle="Edit Client Preference"
                    primaryButtonLabel="Save"
                    secondaryButtonLabel="Cancel"
                    width={800}
                    isOpen={isOpen}
                    onSecondaryButtonClick={() => {
                        formik.handleReset();
                        onClose();
                    }}
                    isPrimaryButtonDisabled={
                        formik.isSubmitting || !formik.dirty || !formik.isValid
                    }
                    dialogContent={
                        <>
                            <p className="margin__bottom--medium">
                                Select payer segments to use <strong>{description}</strong> as their
                                default product for this therapy group.
                            </p>
                            <Checkbox
                                className="margin__bottom--medium"
                                name="isGlobalDefault"
                                label="Global default"
                                checked={formik.values.isGlobalDefault}
                                disabled={isCurrentGlobal}
                                onChange={(checked) =>
                                    formik.setFieldValue('isGlobalDefault', checked)
                                }
                            />
                            {formik.values.isGlobalDefault &&
                                !isCurrentGlobal &&
                                preferences.global && (
                                    <div className="margin__bottom--small">
                                        <InlineMessage
                                            intent={InlineMessage.Intent.WARNING}
                                            wrapText
                                            label={`A global default already exists. ${description} will replace ${replacementDescription}.`}
                                        />
                                    </div>
                                )}
                            <PreferenceWindowRows
                                options={options}
                                isDisabled={formik.values.isGlobalDefault}
                            />
                        </>
                    }
                />
            )}
        </Formik>
    );
};

EditClientPreferenceDialog.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    oid: PropTypes.string,
    parentProducts: PropTypes.arrayOf(PropTypes.shape({})),
    preferences: PropTypes.shape({
        global: PropTypes.string,
        segments: PropTypes.objectOf(PropTypes.string).isRequired,
    }).isRequired,
    payerSegments: PropTypes.arrayOf(PropTypes.shape({})),
    windows: PropTypes.arrayOf(
        PropTypes.shape({
            preferenceId: PropTypes.string,
            segmentId: PropTypes.string,
            oid: PropTypes.string,
            start: PropTypes.string.isRequired,
            end: PropTypes.string,
        }),
    ),
    onApply: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
};
